import type { CrewFile, CrewLookups, LookupItem } from './types';

const toDateInput = (v?: string | null) => (v ? String(v).slice(0, 10) : '');

const toNum = (v: string | number | null | undefined): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const toNumOrNull = (v: string | number | null | undefined): number | null | undefined => {
  if (v === undefined) return undefined;
  if (v === '' || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export type CrewFormCertRow = {
  key: string;
  certId: string;
  issuedDate: string;
  expiredDate: string;
  certNumber: string;
  issueCountryId: string;
  certFileName: string;
  onVessel: boolean;
  notes: string;
};

export type CrewFormAllotteeRow = {
  key: string;
  fullName: string;
  relationId: string;
  homeAddress: string;
  phone: string;
  mobile: string;
  email: string;
  bank: string;
  bankAccount: string;
  amount: string;
  currency: string;
  startDate: string;
  endDate: string;
  frequency: string;
  columnId: string;
};

export type CrewFormAppraisalAnswerRow = {
  key: string;
  questionId: string;
  questionLabelSnapshot: string;
  answerTypeSnapshot: 'SCALE' | 'YES_NO_NA' | 'TEXT';
  scaleMinSnapshot: string;
  scaleMaxSnapshot: string;
  scaleStepSnapshot: string;
  scaleValue: string;
  choiceValue: string;
  textValue: string;
  comment: string;
};

export type CrewFormAppraisalRow = {
  key: string;
  vesselId: string;
  assessmentDate: string;
  assessorName: string;
  notes: string;
  employStatusId: string;
  appraisalType: string;
  kindId: string;
  questionnaireId: string;
  questionnaireAvg: string;
  badHabits: string;
  toBePromoted: boolean;
  dismissalReasonId: string;
  dismissalReasonText: string;
  appraisalFileName: string;
  answers: CrewFormAppraisalAnswerRow[];
};

export type CrewFormFeedbackRow = {
  key: string;
  feedbackDate: string;
  notes: string;
};

export type CrewFormNokRow = {
  key: string;
  fullName: string;
  relationId: string;
  homeAddress: string;
  phone: string;
  mobile: string;
  email: string;
  notes: string;
};

export type CrewFormPrevServiceRow = {
  key: string;
  vesselName: string;
  rankId: string;
  startDate: string;
  signOnDate: string;
  signOnPort: string;
  signOffDate: string;
  endDate: string;
  signOffPort: string;
  signOffReasonId: string;
  managerName: string;
  manningAgency: string;
  vesselTypeId: string;
  engTypeId: string;
  vesselFlagId: string;
  engType: string;
  dwt: string;
  grt: string;
  nrt: string;
  notes: string;
};

export type CrewFormTransactionRow = {
  key: string;
  vesselId: string;
  vesselName: string;
  rankId: string;
  startDate: string;
  signOnDate: string;
  signOnPort: string;
  signOffDate: string;
  endDate: string;
  signOffPort: string;
  signOffReasonId: string;
  manningAgencyId: string;
  coeDate: string;
  extCoeDate: string;
  totalWage: string;
  status: string;
  wageCurrency: string;
  flWatchkeeper: boolean;
  duties: string;
  priorCoeDate: string;
  latestCoeDate: string;
  includedOt: string;
  intentionSignOffDate: string;
  fileName: string;
  notes: string;
  wageScaleId: string;
  accruedLeaveDays: string;
  leavePayDue: string;
  finalCashDue: string;
  repatriation: string;
  /** columnId -> amount string */
  wageValues: Record<string, string>;
};

export type CrewFormMedicalRow = {
  key: string;
  kind: string;
  examDate: string;
  nextDueDate: string;
  result: string;
  resultNotes: string;
  clinicName: string;
  certificateNo: string;
  notes: string;
};

export type CrewFormState = {
  lastName: string;
  middleName: string;
  firstName: string;
  rankId: string;
  nationalityId: string;
  fatherName: string;
  motherName: string;
  sexTypeId: string;
  birthDate: string;
  birthPlace: string;
  telephone1: string;
  telephone2: string;
  fax1: string;
  mobile1: string;
  mobile2: string;
  email1: string;
  email2: string;
  maritalStatusId: string;
  religionId: string;
  lastApplicationDate: string;
  lastContactedDate: string;
  eyeColor: string;
  hairColor: string;
  shoesSize: string;
  workingClothesSize: string;
  homeAddress: string;
  countryId: string;
  nearestPortId: string;
  crewNotes: string;
  crewHeight: string;
  crewWeight: string;
  tempAddress: string;
  noOfChildren: string;
  wifeName: string;
  manningAgentId: string;
  nearestAirport: string;
  notRecommended: boolean;
  availableFrom: string;
  availableTo: string;
  crewRef: string;
  skypeAccount: string;
  bankAccount: string;
  duties: string;
  availabilityStatusId: string;
  availabilityNotes: string;
  certs: CrewFormCertRow[];
  allottees: CrewFormAllotteeRow[];
  appraisals: CrewFormAppraisalRow[];
  feedback: CrewFormFeedbackRow[];
  nextOfKin: CrewFormNokRow[];
  prevServices: CrewFormPrevServiceRow[];
  medicals: CrewFormMedicalRow[];
  transactions: CrewFormTransactionRow[];
};

const newKey = () => `row-${Date.now()}-${Math.random()}`;

const mapAppraisalAnswersPayload = (answers: CrewFormAppraisalAnswerRow[]) =>
  answers
    .filter((ans) => ans.questionId)
    .map((ans) => ({
      questionId: Number(ans.questionId),
      questionLabelSnapshot: ans.questionLabelSnapshot.trim() || 'Question',
      answerTypeSnapshot: ans.answerTypeSnapshot,
      scaleMinSnapshot: toNum(ans.scaleMinSnapshot),
      scaleMaxSnapshot: toNum(ans.scaleMaxSnapshot),
      scaleStepSnapshot: toNum(ans.scaleStepSnapshot),
      scaleValue: toNum(ans.scaleValue),
      choiceValue: (ans.choiceValue as 'YES' | 'NO' | 'NA' | undefined) || undefined,
      textValue: ans.textValue?.trim() || undefined,
      comment: ans.comment?.trim() || undefined,
    }));

const computeFormQuestionnaireAvg = (answers: CrewFormAppraisalAnswerRow[]): number | undefined => {
  const norms: number[] = [];
  for (const a of answers) {
    if (a.answerTypeSnapshot !== 'SCALE' || !a.scaleValue) continue;
    const min = toNum(a.scaleMinSnapshot);
    const max = toNum(a.scaleMaxSnapshot);
    const val = toNum(a.scaleValue);
    if (min == null || max == null || val == null || max <= min) continue;
    norms.push(((val - min) / (max - min)) * 100);
  }
  if (!norms.length) return undefined;
  return Math.round((norms.reduce((sum, value) => sum + value, 0) / norms.length) * 100) / 100;
};

export const emptyCrewForm = (): CrewFormState => ({
  lastName: '',
  middleName: '',
  firstName: '',
  rankId: '',
  nationalityId: '',
  fatherName: '',
  motherName: '',
  sexTypeId: '',
  birthDate: '',
  birthPlace: '',
  telephone1: '',
  telephone2: '',
  fax1: '',
  mobile1: '',
  mobile2: '',
  email1: '',
  email2: '',
  maritalStatusId: '',
  religionId: '',
  lastApplicationDate: '',
  lastContactedDate: '',
  eyeColor: '',
  hairColor: '',
  shoesSize: '',
  workingClothesSize: '',
  homeAddress: '',
  countryId: '',
  nearestPortId: '',
  crewNotes: '',
  crewHeight: '',
  crewWeight: '',
  tempAddress: '',
  noOfChildren: '',
  wifeName: '',
  manningAgentId: '',
  nearestAirport: '',
  notRecommended: false,
  availableFrom: '',
  availableTo: '',
  crewRef: '',
  skypeAccount: '',
  bankAccount: '',
  duties: '',
  availabilityStatusId: '',
  availabilityNotes: '',
  certs: [],
  allottees: [],
  appraisals: [],
  feedback: [],
  nextOfKin: [],
  prevServices: [],
  medicals: [],
  transactions: [],
});

export const crewFileToForm = (item: CrewFile): CrewFormState => ({
  lastName: item.lastName ?? '',
  middleName: item.middleName ?? '',
  firstName: item.firstName ?? '',
  rankId: item.rank?.id != null ? String(item.rank.id) : '',
  nationalityId: item.nationality?.id != null ? String(item.nationality.id) : '',
  fatherName: item.fatherName ?? '',
  motherName: item.motherName ?? '',
  sexTypeId: item.sexType?.id != null ? String(item.sexType.id) : '',
  birthDate: toDateInput(item.birthDate),
  birthPlace: item.birthPlace ?? '',
  telephone1: item.telephone1 ?? '',
  telephone2: item.telephone2 ?? '',
  fax1: item.fax1 ?? '',
  mobile1: item.mobile1 ?? '',
  mobile2: item.mobile2 ?? '',
  email1: item.email1 ?? '',
  email2: item.email2 ?? '',
  maritalStatusId: item.maritalStatus?.id != null ? String(item.maritalStatus.id) : '',
  religionId: item.religion?.id != null ? String(item.religion.id) : '',
  lastApplicationDate: toDateInput(item.lastApplicationDate),
  lastContactedDate: toDateInput(item.lastContactedDate),
  eyeColor: item.eyeColor ?? '',
  hairColor: item.hairColor ?? '',
  shoesSize: item.shoesSize ?? '',
  workingClothesSize: item.workingClothesSize ?? '',
  homeAddress: item.homeAddress ?? '',
  countryId: item.country?.id != null ? String(item.country.id) : '',
  nearestPortId: item.nearestPort?.id != null ? String(item.nearestPort.id) : '',
  crewNotes: item.crewNotes ?? '',
  crewHeight: item.crewHeight ?? '',
  crewWeight: item.crewWeight ?? '',
  tempAddress: item.tempAddress ?? '',
  noOfChildren: item.noOfChildren != null ? String(item.noOfChildren) : '',
  wifeName: item.wifeName ?? '',
  manningAgentId: item.manningAgent?.id != null ? String(item.manningAgent.id) : '',
  nearestAirport: item.nearestAirport ?? '',
  notRecommended: item.notRecommended ?? false,
  availableFrom: toDateInput(item.availableFrom),
  availableTo: toDateInput(item.availableTo),
  crewRef: item.crewRef ?? '',
  skypeAccount: item.skypeAccount ?? '',
  bankAccount: item.bankAccount ?? '',
  duties: item.duties ?? '',
  availabilityStatusId: item.availabilityStatus?.id != null ? String(item.availabilityStatus.id) : '',
  availabilityNotes: item.availabilityNotes ?? '',
  certs: (item.certs ?? []).map((c) => ({
    key: newKey(),
    certId: String(c.certId),
    issuedDate: toDateInput(c.issuedDate),
    expiredDate: toDateInput(c.expiredDate),
    certNumber: c.certNumber ?? '',
    issueCountryId: c.issueCountryId != null ? String(c.issueCountryId) : '',
    certFileName: c.certFileName ?? '',
    onVessel: c.onVessel ?? false,
    notes: c.notes ?? '',
  })),
  allottees: (item.allottees ?? []).map((a) => ({
    key: newKey(),
    fullName: a.fullName ?? '',
    relationId: a.relationId != null ? String(a.relationId) : '',
    homeAddress: a.homeAddress ?? '',
    phone: a.phone ?? '',
    mobile: a.mobile ?? '',
    email: a.email ?? '',
    bank: a.bank ?? '',
    bankAccount: a.bankAccount ?? '',
    amount: a.amount != null ? String(a.amount) : '',
    currency: a.currency ?? '',
    startDate: toDateInput(a.startDate),
    endDate: toDateInput(a.endDate),
    frequency: a.frequency ?? '',
    columnId: a.columnId != null ? String(a.columnId) : '',
  })),
  appraisals: (item.appraisals ?? []).map((a) => ({
    key: newKey(),
    vesselId: a.vesselId != null ? String(a.vesselId) : '',
    assessmentDate: toDateInput(a.assessmentDate),
    assessorName: a.assessorName ?? '',
    notes: a.notes ?? '',
    employStatusId: a.employStatusId != null ? String(a.employStatusId) : '',
    appraisalType: a.appraisalType ?? a.kind?.code ?? '',
    kindId: a.kindId != null ? String(a.kindId) : a.kind?.id != null ? String(a.kind.id) : '',
    questionnaireId:
      a.questionnaireId != null ? String(a.questionnaireId) : a.questionnaire?.id != null ? String(a.questionnaire.id) : '',
    questionnaireAvg: a.questionnaireAvg != null ? String(a.questionnaireAvg) : '',
    badHabits: a.badHabits ?? '',
    toBePromoted: a.toBePromoted ?? false,
    dismissalReasonId: a.dismissalReasonId != null ? String(a.dismissalReasonId) : '',
    dismissalReasonText: a.dismissalReasonText ?? '',
    appraisalFileName: a.appraisalFileName ?? '',
    answers: (a.answers ?? []).map((ans) => ({
      key: newKey(),
      questionId: String(ans.questionId),
      questionLabelSnapshot: ans.questionLabelSnapshot ?? ans.question?.label ?? '',
      answerTypeSnapshot: ans.answerTypeSnapshot ?? ans.question?.answerType ?? 'TEXT',
      scaleMinSnapshot:
        ans.scaleMinSnapshot != null ? String(ans.scaleMinSnapshot) : ans.question?.scaleMin != null ? String(ans.question.scaleMin) : '',
      scaleMaxSnapshot:
        ans.scaleMaxSnapshot != null ? String(ans.scaleMaxSnapshot) : ans.question?.scaleMax != null ? String(ans.question.scaleMax) : '',
      scaleStepSnapshot:
        ans.scaleStepSnapshot != null ? String(ans.scaleStepSnapshot) : ans.question?.scaleStep != null ? String(ans.question.scaleStep) : '',
      scaleValue: ans.scaleValue != null ? String(ans.scaleValue) : '',
      choiceValue: ans.choiceValue ?? '',
      textValue: ans.textValue ?? '',
      comment: ans.comment ?? '',
    })),
  })),
  feedback: (item.feedback ?? []).map((f) => ({
    key: newKey(),
    feedbackDate: toDateInput(f.feedbackDate),
    notes: f.notes ?? '',
  })),
  nextOfKin: (item.nextOfKin ?? []).map((n) => ({
    key: newKey(),
    fullName: n.fullName ?? '',
    relationId: n.relationId != null ? String(n.relationId) : '',
    homeAddress: n.homeAddress ?? '',
    phone: n.phone ?? '',
    mobile: n.mobile ?? '',
    email: n.email ?? '',
    notes: n.notes ?? '',
  })),
  prevServices: (item.prevServices ?? []).map((p) => ({
    key: newKey(),
    vesselName: p.vesselName ?? '',
    rankId: p.rankId != null ? String(p.rankId) : '',
    startDate: toDateInput(p.startDate),
    signOnDate: toDateInput(p.signOnDate),
    signOnPort: p.signOnPort ?? '',
    signOffDate: toDateInput(p.signOffDate),
    endDate: toDateInput(p.endDate),
    signOffPort: p.signOffPort ?? '',
    signOffReasonId: p.signOffReasonId != null ? String(p.signOffReasonId) : '',
    managerName: p.managerName ?? '',
    manningAgency: p.manningAgency ?? '',
    vesselTypeId: p.vesselTypeId != null ? String(p.vesselTypeId) : '',
    engTypeId: p.engTypeId != null ? String(p.engTypeId) : '',
    vesselFlagId: p.vesselFlagId != null ? String(p.vesselFlagId) : '',
    engType: p.engType ?? '',
    dwt: p.dwt != null ? String(p.dwt) : '',
    grt: p.grt != null ? String(p.grt) : '',
    nrt: p.nrt != null ? String(p.nrt) : '',
    notes: p.notes ?? '',
  })),
  transactions: (item.transactions ?? []).map((t) => {
    const wageValues: Record<string, string> = {};
    for (const wv of t.wageValues ?? []) {
      wageValues[String(wv.columnId)] = wv.amount != null ? String(wv.amount) : '';
    }
    return {
      key: newKey(),
      vesselId: t.vesselId != null ? String(t.vesselId) : '',
      vesselName: t.vesselName ?? '',
      rankId: t.rankId != null ? String(t.rankId) : '',
      startDate: toDateInput(t.startDate),
      signOnDate: toDateInput(t.signOnDate),
      signOnPort: t.signOnPort ?? '',
      signOffDate: toDateInput(t.signOffDate),
      endDate: toDateInput(t.endDate),
      signOffPort: t.signOffPort ?? '',
      signOffReasonId: t.signOffReasonId != null ? String(t.signOffReasonId) : '',
      manningAgencyId: t.manningAgencyId != null ? String(t.manningAgencyId) : '',
      coeDate: toDateInput(t.coeDate),
      extCoeDate: toDateInput(t.extCoeDate),
      totalWage: t.totalWage != null ? String(t.totalWage) : '',
      status: t.status ?? '',
      wageCurrency: t.wageCurrency ?? '',
      flWatchkeeper: t.flWatchkeeper ?? false,
      duties: t.duties ?? '',
      priorCoeDate: toDateInput(t.priorCoeDate),
      latestCoeDate: toDateInput(t.latestCoeDate),
      includedOt: t.includedOt != null ? String(t.includedOt) : '',
      intentionSignOffDate: toDateInput(t.intentionSignOffDate),
      fileName: t.fileName ?? '',
      notes: t.notes ?? '',
      wageScaleId: t.wageScaleId != null ? String(t.wageScaleId) : '',
      accruedLeaveDays: t.accruedLeaveDays != null ? String(t.accruedLeaveDays) : '',
      leavePayDue: t.leavePayDue != null ? String(t.leavePayDue) : '',
      finalCashDue: t.finalCashDue != null ? String(t.finalCashDue) : '',
      repatriation: t.repatriation ?? '',
      wageValues,
    };
  }),
  medicals: (item.medicals ?? []).map((m) => ({
    key: newKey(),
    kind: m.kind ?? 'FITNESS',
    examDate: toDateInput(m.examDate),
    nextDueDate: toDateInput(m.nextDueDate),
    result: m.result ?? '',
    resultNotes: m.resultNotes ?? '',
    clinicName: m.clinicName ?? '',
    certificateNo: m.certificateNo ?? '',
    notes: m.notes ?? '',
  })),
});

export const formToPayload = (form: CrewFormState) => ({
  lastName: form.lastName.trim(),
  middleName: form.middleName.trim() || null,
  firstName: form.firstName.trim(),
  rankId: toNumOrNull(form.rankId),
  nationalityId: toNumOrNull(form.nationalityId),
  fatherName: form.fatherName.trim() || null,
  motherName: form.motherName.trim() || null,
  sexTypeId: toNumOrNull(form.sexTypeId),
  birthDate: form.birthDate || null,
  birthPlace: form.birthPlace.trim() || null,
  telephone1: form.telephone1.trim() || null,
  telephone2: form.telephone2.trim() || null,
  fax1: form.fax1.trim() || null,
  mobile1: form.mobile1.trim() || null,
  mobile2: form.mobile2.trim() || null,
  email1: form.email1.trim() || null,
  email2: form.email2.trim() || null,
  maritalStatusId: toNumOrNull(form.maritalStatusId),
  religionId: toNumOrNull(form.religionId),
  lastApplicationDate: form.lastApplicationDate || null,
  lastContactedDate: form.lastContactedDate || null,
  eyeColor: form.eyeColor.trim() || null,
  hairColor: form.hairColor.trim() || null,
  shoesSize: form.shoesSize.trim() || null,
  workingClothesSize: form.workingClothesSize.trim() || null,
  homeAddress: form.homeAddress.trim() || null,
  countryId: toNumOrNull(form.countryId),
  nearestPortId: toNumOrNull(form.nearestPortId),
  crewNotes: form.crewNotes.trim() || null,
  crewHeight: form.crewHeight.trim() || null,
  crewWeight: form.crewWeight.trim() || null,
  tempAddress: form.tempAddress.trim() || null,
  noOfChildren: toNumOrNull(form.noOfChildren),
  wifeName: form.wifeName.trim() || null,
  manningAgentId: toNumOrNull(form.manningAgentId),
  nearestAirport: form.nearestAirport.trim() || null,
  notRecommended: form.notRecommended,
  availableFrom: form.availableFrom || null,
  availableTo: form.availableTo || null,
  crewRef: form.crewRef.trim() || null,
  skypeAccount: form.skypeAccount.trim() || null,
  bankAccount: form.bankAccount.trim() || null,
  duties: form.duties.trim() || null,
  availabilityStatusId: toNumOrNull(form.availabilityStatusId),
  availabilityNotes: form.availabilityNotes.trim() || null,
  certs: form.certs
    .filter((c) => c.certId)
    .map((c) => ({
      certId: Number(c.certId),
      issuedDate: c.issuedDate || undefined,
      expiredDate: c.expiredDate || undefined,
      certNumber: c.certNumber?.trim() || undefined,
      issueCountryId: toNum(c.issueCountryId),
      certFileName: c.certFileName?.trim() || undefined,
      onVessel: c.onVessel ?? false,
      notes: c.notes?.trim() || undefined,
    })),
  allottees: form.allottees
    .filter((a) => a.fullName.trim())
    .map((a) => ({
      fullName: a.fullName.trim(),
      relationId: toNum(a.relationId),
      homeAddress: a.homeAddress?.trim() || undefined,
      phone: a.phone?.trim() || undefined,
      mobile: a.mobile?.trim() || undefined,
      email: a.email?.trim() || undefined,
      bank: a.bank?.trim() || undefined,
      bankAccount: a.bankAccount?.trim() || undefined,
      amount: toNum(a.amount),
      currency: a.currency?.trim() || undefined,
      startDate: a.startDate || undefined,
      endDate: a.endDate || undefined,
      frequency: a.frequency?.trim() || undefined,
      columnId: toNum(a.columnId),
    })),
  appraisals: form.appraisals.map((a) => ({
    vesselId: toNum(a.vesselId),
    assessmentDate: a.assessmentDate || undefined,
    assessorName: a.assessorName?.trim() || undefined,
    notes: a.notes?.trim() || undefined,
    employStatusId: toNum(a.employStatusId),
    appraisalType: a.appraisalType || undefined,
    kindId: toNum(a.kindId),
    questionnaireId: toNum(a.questionnaireId),
    questionnaireAvg: computeFormQuestionnaireAvg(a.answers ?? []),
    badHabits: a.badHabits?.trim() || undefined,
    toBePromoted: a.toBePromoted ?? false,
    dismissalReasonId: toNum(a.dismissalReasonId),
    dismissalReasonText: a.dismissalReasonText?.trim() || undefined,
    appraisalFileName: a.appraisalFileName?.trim() || undefined,
    answers: mapAppraisalAnswersPayload(a.answers ?? []),
  })),
  feedback: form.feedback.map((f) => ({
    feedbackDate: f.feedbackDate || undefined,
    notes: f.notes?.trim() || undefined,
  })),
  nextOfKin: form.nextOfKin
    .filter((n) => n.fullName.trim())
    .map((n) => ({
      fullName: n.fullName.trim(),
      relationId: toNum(n.relationId),
      homeAddress: n.homeAddress?.trim() || undefined,
      phone: n.phone?.trim() || undefined,
      mobile: n.mobile?.trim() || undefined,
      email: n.email?.trim() || undefined,
      notes: n.notes?.trim() || undefined,
    })),
  prevServices: form.prevServices.map((p) => ({
    vesselName: p.vesselName?.trim() || undefined,
    rankId: toNum(p.rankId),
    startDate: p.startDate || undefined,
    signOnDate: p.signOnDate || undefined,
    signOnPort: p.signOnPort?.trim() || undefined,
    signOffDate: p.signOffDate || undefined,
    endDate: p.endDate || undefined,
    signOffPort: p.signOffPort?.trim() || undefined,
    signOffReasonId: toNum(p.signOffReasonId),
    managerName: p.managerName?.trim() || undefined,
    manningAgency: p.manningAgency?.trim() || undefined,
    vesselTypeId: toNum(p.vesselTypeId),
    engTypeId: toNum(p.engTypeId),
    vesselFlagId: toNum(p.vesselFlagId),
    engType: p.engType?.trim() || undefined,
    dwt: toNum(p.dwt),
    grt: toNum(p.grt),
    nrt: toNum(p.nrt),
    notes: p.notes?.trim() || undefined,
  })),
  transactions: form.transactions.map((t) => ({
    vesselId: toNum(t.vesselId),
    vesselName: t.vesselName?.trim() || undefined,
    rankId: toNum(t.rankId),
    startDate: t.startDate || undefined,
    signOnDate: t.signOnDate || undefined,
    signOnPort: t.signOnPort?.trim() || undefined,
    signOffDate: t.signOffDate || undefined,
    endDate: t.endDate || undefined,
    signOffPort: t.signOffPort?.trim() || undefined,
    signOffReasonId: toNum(t.signOffReasonId),
    manningAgencyId: toNum(t.manningAgencyId),
    coeDate: t.coeDate || undefined,
    extCoeDate: t.extCoeDate || undefined,
    totalWage: toNum(t.totalWage),
    status: t.status || undefined,
    wageCurrency: t.wageCurrency?.trim() || undefined,
    flWatchkeeper: t.flWatchkeeper ?? false,
    duties: t.duties?.trim() || undefined,
    priorCoeDate: t.priorCoeDate || undefined,
    latestCoeDate: t.latestCoeDate || undefined,
    includedOt: toNum(t.includedOt),
    intentionSignOffDate: t.intentionSignOffDate || undefined,
    fileName: t.fileName?.trim() || undefined,
    notes: t.notes?.trim() || undefined,
    wageScaleId: toNum(t.wageScaleId),
    accruedLeaveDays: toNum(t.accruedLeaveDays),
    leavePayDue: toNum(t.leavePayDue),
    finalCashDue: toNum(t.finalCashDue),
    repatriation: t.repatriation?.trim() || undefined,
    wageValues: Object.entries(t.wageValues || {})
      .filter(([, amount]) => amount !== '')
      .map(([columnId, amount]) => ({
        columnId: Number(columnId),
        amount: Number(amount),
        source: t.wageScaleId ? 'SCALE' : 'MANUAL',
      })),
  })),
  medicals: form.medicals.map((m) => ({
    kind: m.kind || undefined,
    examDate: m.examDate || undefined,
    nextDueDate: m.nextDueDate || undefined,
    result: m.result || undefined,
    resultNotes: m.resultNotes?.trim() || undefined,
    clinicName: m.clinicName?.trim() || undefined,
    certificateNo: m.certificateNo?.trim() || undefined,
    notes: m.notes?.trim() || undefined,
  })),
});

const lookupById = (items: LookupItem[] | undefined, id: string): LookupItem | null => {
  if (!id.trim()) return null;
  return items?.find((i) => String(i.id) === id) ?? null;
};

const emptyIso = () => new Date().toISOString();

/** Build a CrewFile-shaped object from in-progress form state for the live dashboard preview. */
export const formToDashboardPreview = (
  form: CrewFormState,
  lookups: CrewLookups | undefined,
  crewId = 0,
): CrewFile => ({
  id: crewId,
  lastName: form.lastName,
  firstName: form.firstName,
  middleName: form.middleName.trim() || null,
  crewRef: form.crewRef.trim() || null,
  rank: lookupById(lookups?.ranks, form.rankId),
  nationality: lookupById(lookups?.nationalities, form.nationalityId),
  availabilityStatus: lookupById(lookups?.availabilityStatuses, form.availabilityStatusId),
  sexType: lookupById(lookups?.sexTypes, form.sexTypeId),
  maritalStatus: lookupById(lookups?.maritalStatuses, form.maritalStatusId),
  religion: lookupById(lookups?.religions, form.religionId),
  manningAgent: lookupById(lookups?.manningAgents, form.manningAgentId),
  country: form.countryId
    ? lookups?.countries?.find((c) => String(c.id) === form.countryId) ?? null
    : null,
  nearestPort: form.nearestPortId
    ? lookups?.ports?.find((p) => String(p.id) === form.nearestPortId) ?? null
    : null,
  fatherName: form.fatherName.trim() || null,
  motherName: form.motherName.trim() || null,
  birthDate: form.birthDate || null,
  birthPlace: form.birthPlace.trim() || null,
  telephone1: form.telephone1.trim() || null,
  telephone2: form.telephone2.trim() || null,
  fax1: form.fax1.trim() || null,
  mobile1: form.mobile1.trim() || null,
  mobile2: form.mobile2.trim() || null,
  email1: form.email1.trim() || null,
  email2: form.email2.trim() || null,
  lastApplicationDate: form.lastApplicationDate || null,
  lastContactedDate: form.lastContactedDate || null,
  eyeColor: form.eyeColor.trim() || null,
  hairColor: form.hairColor.trim() || null,
  shoesSize: form.shoesSize.trim() || null,
  workingClothesSize: form.workingClothesSize.trim() || null,
  homeAddress: form.homeAddress.trim() || null,
  crewNotes: form.crewNotes.trim() || null,
  crewHeight: form.crewHeight.trim() || null,
  crewWeight: form.crewWeight.trim() || null,
  tempAddress: form.tempAddress.trim() || null,
  noOfChildren: toNumOrNull(form.noOfChildren) ?? null,
  wifeName: form.wifeName.trim() || null,
  nearestAirport: form.nearestAirport.trim() || null,
  notRecommended: form.notRecommended,
  availableFrom: form.availableFrom || null,
  availableTo: form.availableTo || null,
  skypeAccount: form.skypeAccount.trim() || null,
  bankAccount: form.bankAccount.trim() || null,
  duties: form.duties.trim() || null,
  availabilityNotes: form.availabilityNotes.trim() || null,
  createdAt: emptyIso(),
  updatedAt: emptyIso(),
  certs: form.certs
    .filter((c) => c.certId)
    .map((c) => ({
      certId: Number(c.certId),
      issuedDate: c.issuedDate || null,
      expiredDate: c.expiredDate || null,
      certNumber: c.certNumber?.trim() || null,
      issueCountryId: toNum(c.issueCountryId) ?? null,
      certFileName: c.certFileName?.trim() || null,
      onVessel: c.onVessel ?? false,
      notes: c.notes?.trim() || null,
      certCatalog: lookupById(lookups?.certCatalogs, c.certId) ?? undefined,
      issueCountry: c.issueCountryId
        ? lookups?.countries?.find((co) => String(co.id) === c.issueCountryId) ?? null
        : null,
    })),
  allottees: form.allottees
    .filter((a) => a.fullName.trim())
    .map((a) => ({
      fullName: a.fullName.trim(),
      relationId: toNum(a.relationId) ?? null,
      homeAddress: a.homeAddress?.trim() || null,
      phone: a.phone?.trim() || null,
      mobile: a.mobile?.trim() || null,
      email: a.email?.trim() || null,
      bank: a.bank?.trim() || null,
      bankAccount: a.bankAccount?.trim() || null,
      amount: toNum(a.amount) ?? null,
      relation: lookupById(lookups?.relations, a.relationId),
    })),
  appraisals: form.appraisals.map((a) => ({
    vesselId: toNum(a.vesselId) ?? null,
    assessmentDate: a.assessmentDate || null,
    assessorName: a.assessorName?.trim() || null,
    notes: a.notes?.trim() || null,
    employStatusId: toNum(a.employStatusId) ?? null,
    appraisalType: (a.appraisalType as 'APPRAISAL' | 'DISCHARGE') || null,
    kindId: toNum(a.kindId) ?? null,
    questionnaireId: toNum(a.questionnaireId) ?? null,
    questionnaireAvg: computeFormQuestionnaireAvg(a.answers ?? []) ?? null,
    badHabits: a.badHabits?.trim() || null,
    toBePromoted: a.toBePromoted ?? false,
    dismissalReasonId: toNum(a.dismissalReasonId) ?? null,
    dismissalReasonText: a.dismissalReasonText?.trim() || null,
    appraisalFileName: a.appraisalFileName?.trim() || null,
    vessel: a.vesselId
      ? lookups?.vessels?.find((v) => String(v.id) === a.vesselId) ?? null
      : null,
    employmentStatus: lookupById(lookups?.employmentStatuses, a.employStatusId),
    signOffReason: lookupById(lookups?.signOffReasons, a.dismissalReasonId),
    kind: lookupById(lookups?.questionnaireKinds, a.kindId),
    questionnaire: a.questionnaireId
      ? lookups?.questionnaires?.find((q) => String(q.id) === a.questionnaireId) ?? null
      : null,
    answers: mapAppraisalAnswersPayload(a.answers ?? []).map((ans) => ({
      ...ans,
      scaleMinSnapshot: ans.scaleMinSnapshot ?? null,
      scaleMaxSnapshot: ans.scaleMaxSnapshot ?? null,
      scaleStepSnapshot: ans.scaleStepSnapshot ?? null,
      scaleValue: ans.scaleValue ?? null,
      choiceValue: ans.choiceValue ?? null,
      textValue: ans.textValue ?? null,
      comment: ans.comment ?? null,
    })),
  })),
  feedback: form.feedback.map((f) => ({
    feedbackDate: f.feedbackDate || null,
    notes: f.notes?.trim() || null,
  })),
  nextOfKin: form.nextOfKin
    .filter((n) => n.fullName.trim())
    .map((n) => ({
      fullName: n.fullName.trim(),
      relationId: toNum(n.relationId) ?? null,
      homeAddress: n.homeAddress?.trim() || null,
      phone: n.phone?.trim() || null,
      mobile: n.mobile?.trim() || null,
      email: n.email?.trim() || null,
      notes: n.notes?.trim() || null,
      relation: lookupById(lookups?.relations, n.relationId),
    })),
  prevServices: form.prevServices.map((p) => ({
    vesselName: p.vesselName?.trim() || null,
    rankId: toNum(p.rankId) ?? null,
    startDate: p.startDate || null,
    signOnDate: p.signOnDate || null,
    signOnPort: p.signOnPort?.trim() || null,
    signOffDate: p.signOffDate || null,
    endDate: p.endDate || null,
    signOffPort: p.signOffPort?.trim() || null,
    signOffReasonId: toNum(p.signOffReasonId) ?? null,
    managerName: p.managerName?.trim() || null,
    manningAgency: p.manningAgency?.trim() || null,
    vesselTypeId: toNum(p.vesselTypeId) ?? null,
    engTypeId: toNum(p.engTypeId) ?? null,
    vesselFlagId: toNum(p.vesselFlagId) ?? null,
    engType: p.engType?.trim() || null,
    dwt: toNum(p.dwt) ?? null,
    grt: toNum(p.grt) ?? null,
    nrt: toNum(p.nrt) ?? null,
    notes: p.notes?.trim() || null,
    rank: lookupById(lookups?.ranks, p.rankId),
    signOffReason: lookupById(lookups?.signOffReasons, p.signOffReasonId),
    vesselType: lookupById(lookups?.vesselTypes, p.vesselTypeId),
    engineType: lookupById(lookups?.engineTypes, p.engTypeId),
    vesselFlag: p.vesselFlagId
      ? lookups?.countries?.find((c) => String(c.id) === p.vesselFlagId) ?? null
      : null,
  })),
  transactions: form.transactions.map((t) => ({
    vesselId: toNum(t.vesselId) ?? null,
    vesselName: t.vesselName?.trim() || null,
    rankId: toNum(t.rankId) ?? null,
    startDate: t.startDate || null,
    signOnDate: t.signOnDate || null,
    signOnPort: t.signOnPort?.trim() || null,
    signOffDate: t.signOffDate || null,
    endDate: t.endDate || null,
    signOffPort: t.signOffPort?.trim() || null,
    signOffReasonId: toNum(t.signOffReasonId) ?? null,
    manningAgencyId: toNum(t.manningAgencyId) ?? null,
    coeDate: t.coeDate || null,
    extCoeDate: t.extCoeDate || null,
    totalWage: toNum(t.totalWage) ?? null,
    status: (t.status as 'ONB' | 'OFB') || null,
    wageCurrency: t.wageCurrency?.trim() || null,
    flWatchkeeper: t.flWatchkeeper ?? false,
    duties: t.duties?.trim() || null,
    priorCoeDate: t.priorCoeDate || null,
    latestCoeDate: t.latestCoeDate || null,
    includedOt: toNum(t.includedOt) ?? null,
    intentionSignOffDate: t.intentionSignOffDate || null,
    fileName: t.fileName?.trim() || null,
    notes: t.notes?.trim() || null,
    vessel: t.vesselId
      ? lookups?.vessels?.find((v) => String(v.id) === t.vesselId) ?? null
      : null,
    rank: lookupById(lookups?.ranks, t.rankId),
    signOffReason: lookupById(lookups?.signOffReasons, t.signOffReasonId),
    manningAgent: lookupById(lookups?.manningAgents, t.manningAgencyId),
  })),
});

export const crewDisplayName = (item: Pick<CrewFile, 'lastName' | 'firstName' | 'middleName' | 'crewRef'>) => {
  const parts = [item.firstName, item.middleName, item.lastName].filter(Boolean).join(' ');
  return parts.trim() || item.crewRef || 'Crew file';
};
